import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContactListItem, TypeInteraction } from '@cockpit/shared';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { InteractionForm } from '../components/contacts/InteractionForm';
import { Input } from '../components/ui/Input';
import {
  LABEL_STATUT_PIPELINE,
  LABEL_TERRITOIRE_CONTACT,
  formatDate,
  parseDateToIso,
} from '../lib/format';

export function RelancesPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<'me' | 'all'>('me');
  const [selectedContact, setSelectedContact] = useState<ContactListItem | null>(null);

  const userId = scope === 'me' && user !== null ? user.id : undefined;

  const query = useQuery({
    queryKey: ['contacts', 'relances', userId],
    queryFn: () => api.contacts.relances({ user_id: userId }),
  });

  return (
    <div className="max-w-5xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Relances du jour</h1>
          <p className="text-xs text-slate-500 mt-1">
            Contacts avec une relance prévue aujourd'hui ou en retard.
          </p>
        </div>
        <div className="flex rounded-md overflow-hidden border border-slate-700">
          <button
            type="button"
            onClick={(): void => setScope('me')}
            className={`px-3 py-1.5 text-xs font-medium ${
              scope === 'me'
                ? 'bg-floryon-green text-white'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
            }`}
          >
            Mes relances
          </button>
          <button
            type="button"
            onClick={(): void => setScope('all')}
            className={`px-3 py-1.5 text-xs font-medium ${
              scope === 'all'
                ? 'bg-floryon-green text-white'
                : 'bg-slate-900 text-slate-300 hover:bg-slate-800'
            }`}
          >
            Toutes
          </button>
        </div>
      </header>

      {query.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {query.isError && <div className="text-sm text-red-400">Erreur de chargement.</div>}

      {query.isSuccess && query.data.items.length === 0 && (
        <div className="text-center py-16 bg-slate-900/40 border border-dashed border-slate-800 rounded-xl text-slate-500">
          Aucune relance en retard. 🎉
        </div>
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Relance prévue</th>
                <th className="px-4 py-2 text-left">Contact</th>
                <th className="px-4 py-2 text-left">Statut</th>
                <th className="px-4 py-2 text-left">Territoire</th>
                <th className="px-4 py-2 text-left">Dernière interaction</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {query.data.items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-900/60">
                  <td className="px-4 py-2 text-amber-300 text-sm">
                    {formatDate(c.prochaine_relance)}
                  </td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/contacts/${String(c.id)}`}
                      className="text-slate-100 hover:text-floryon-green"
                    >
                      {c.societe ?? `${c.prenom ?? ''} ${c.nom}`.trim()}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-400">
                    {LABEL_STATUT_PIPELINE[c.statut_pipeline] ?? c.statut_pipeline}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-400">
                    {LABEL_TERRITOIRE_CONTACT[c.territoire] ?? c.territoire}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-400">
                    {formatDate(c.date_derniere_interaction)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" onClick={(): void => setSelectedContact(c)}>
                      Marquer relancé
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RelanceModal
        contact={selectedContact}
        onClose={(): void => setSelectedContact(null)}
        onDone={(): void => {
          setSelectedContact(null);
          void queryClient.invalidateQueries({ queryKey: ['contacts'] });
        }}
      />
    </div>
  );
}

function RelanceModal({
  contact,
  onClose,
  onDone,
}: {
  contact: ContactListItem | null;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [prochaineRelanceDate, setProchaineRelanceDate] = useState('');

  const createInter = useMutation({
    mutationFn: api.interactions.create,
  });
  const updateContact = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof api.contacts.update>[1] }) =>
      api.contacts.update(id, body),
  });

  if (contact === null) return <></>;

  const handleSubmit = async (data: {
    type_interaction: TypeInteraction;
    date_interaction: string;
    resume: string;
    prochaine_etape: string | null;
  }): Promise<void> => {
    await createInter.mutateAsync({ contact_id: contact.id, ...data });
    const newDate = prochaineRelanceDate === '' ? null : parseDateToIso(prochaineRelanceDate);
    await updateContact.mutateAsync({
      id: contact.id,
      body: { prochaine_relance: newDate },
    });
    onDone();
    setProchaineRelanceDate('');
  };

  return (
    <Modal
      isOpen={contact !== null}
      onClose={onClose}
      title={`Relance : ${contact.societe ?? `${contact.prenom ?? ''} ${contact.nom}`.trim()}`}
    >
      <div className="flex flex-col gap-3">
        <InteractionForm
          onSubmit={(d): void => {
            void handleSubmit(d);
          }}
          submitting={createInter.isPending || updateContact.isPending}
          onCancel={onClose}
        />
        <Input
          label="Prochaine relance (laisser vide = pas de prochaine relance)"
          type="date"
          value={prochaineRelanceDate}
          onChange={(e): void => setProchaineRelanceDate(e.target.value)}
        />
      </div>
    </Modal>
  );
}
